// ============================================
// Site Content Data
// All text content for the Bridge LPO website
// ============================================

export const siteInfo = {
  name: 'Bridge LPO',
  tagline: 'Connecting Law Firms and Outsourcing Teams',
  phone: '+1(919) 272-8485',
  phoneDisplay: '+1(919) 272-8485',
  email: 'contact@bridgelpo.com',
  copyright: `Copyright © ${new Date().getFullYear()} Bridge Legal Services. All rights reserved.`,
  footerDescription:
    'Attorney-led teams, 24/7 incident response, and on-demand paralegals — trusted by law firms and corporate legal departments for defensible, high legal operations.',
  businessHours: {
    weekdays: '9:00 AM - 6:00 PM EST',
    saturday: '10:00 AM - 4:00 PM EST',
    sunday: 'Closed',
  },
  // Served from public/, not from an absolute URL on our own domain. The
  // WordPress-hosted originals were lost when this docroot changed hands, and
  // an asset that ships with the build cannot be orphaned that way again.
  // One file for both: the header and footer each render it at 50px tall, and
  // it carries a transparent background so the footer's brightness/invert
  // filter produces a clean white silhouette rather than a solid block.
  logo: '/img/bridge-lpo-logo.png',
  logoFull: '/img/bridge-lpo-logo.png',
};

export const stats = [
  { value: '50K+', label: 'Documents Reviewed Annually' },
  { value: '120+', label: 'Contracts Managed' },
  { value: '4 Years', label: 'Average Paralegal Experience' },
];

export const whyChooseUs = [
  {
    title: 'Expert Professionals',
    description: 'Qualified legal experts with extensive industry experience and proven track records.',
    icon: 'Users',
    image: '/img/choose-1.webp',
  },
  {
    title: 'Trusted Service',
    description: 'Bank-level security and strict confidentiality to protect your sensitive information.',
    icon: 'Shield',
    image: '/img/choose-2.webp',
  },
  {
    title: '24/7 Support',
    description: "Round-the-clock availability ensuring your deadlines are met, no matter the time zone.",
    icon: 'Clock',
    image: '/img/choose-3.webp',
  },
  {
    title: 'Cost-Effective',
    description: 'Reduce operational costs by up to 60% while maintaining exceptional quality.',
    icon: 'DollarSign',
    image: '/img/choose-4.webp',
  },
];

export const practiceAreas = [
  {
    slug: 'personal-injury-paralegal-services',
    title: 'Personal Injury Paralegal Services',
    shortTitle: 'Personal Injury',
    tagline: 'Reliable Personal Injury Paralegal Support for Law Firms & Attorneys',
    description:
      'Our Personal Injury Paralegal Services help law firms manage high-volume caseloads with speed, accuracy, and complete administrative support. We assist attorneys in preparing claims, reviewing medical documents, organizing evidence, and communicating with clients and insurance companies. By streamlining complex tasks, we help law firms deliver faster settlements and improved case outcomes.',
    homeDescription: 'Reliable Personal Injury Paralegal Support for Law Firms & Attorneys',
    image: '/img/services/personal-injury-card.webp',
    detailImage: '/img/services/personal-injury-detail.webp',
    areaImage: '/img/services/personal-injury-banner.webp',
    areaDescription:
      'We provide dependable Personal Injury Paralegal support to help law firms handle complex, high-volume cases efficiently. Our team streamlines documentation, case preparation, and insurer coordination. This allows attorneys to focus on strategy, negotiations, and results.',
    services: [
      { title: 'Medical Records & Summaries', description: 'We collect, review, and summarize medical records for clear claim support.' },
      { title: 'Demand & Settlement Drafting', description: 'Concise demand letters, complaints, and settlement documents that strengthen negotiations.' },
      { title: 'Accident Evidence Support', description: 'Organizing reports, photos, and witness details into litigation-ready files.' },
      { title: 'Insurance Claim Assistance', description: 'Managing insurer communication and follow-ups to speed up claim processing.' },
      { title: 'Discovery & Trial Prep', description: 'Drafting subpoenas, summarizing depositions, and preparing exhibits for court.' },
      { title: 'Lien Management', description: 'Coordinating with healthcare providers to reduce liens and protect settlement amounts.' },
    ],
  },
  {
    slug: 'workers-compensation-paralegal-services',
    title: 'Workers Compensation Paralegal Services',
    shortTitle: "Worker's Compensation",
    tagline: "End-to-End Paralegal Support for Workers' Compensation Cases",
    description:
      "We deliver end-to-end Workers' Compensation paralegal support for law firms and attorneys. Our team manages documentation, medical records, and insurer coordination with strict regulatory compliance. This ensures claims progress efficiently from injury to resolution.",
    homeDescription: "End-to-End Paralegal Support for Workers' Compensation Cases",
    image: '/img/services/workers-compensation-card.webp',
    areaImage: '/img/services/workers-compensation-banner.webp',
    areaDescription:
      "We deliver end-to-end Workers' Compensation paralegal support for law firms and attorneys. Our team manages documentation, medical records, and insurer coordination with strict regulatory compliance. This ensures claims progress efficiently from injury to resolution.",
    services: [
      { title: 'Claims Documentation', description: 'Comprehensive claims filing and documentation management.' },
      { title: 'Medical Records Review', description: 'Detailed review and organization of medical documentation.' },
      { title: 'Insurer Coordination', description: 'Managing all communications with insurance carriers.' },
      { title: 'Regulatory Compliance', description: 'Ensuring all filings meet state and federal requirements.' },
      { title: 'Hearing Preparation', description: 'Preparing case files and exhibits for hearings.' },
      { title: 'Benefits Calculation', description: 'Accurate calculation of compensation and benefits owed.' },
    ],
  },
  {
    slug: 'employment-law-paralegal-support-services',
    title: 'Employment Law Paralegal Support Services',
    shortTitle: 'Employment Law',
    tagline: 'Efficient legal assistance for workplace rights & compliance matters',
    description:
      'We provide efficient Employment Law paralegal support for workplace rights and compliance matters. Our team assists attorneys with documentation, case management, and regulatory filings across employer and employee disputes. This ensures accuracy, timely responses, and streamlined case handling.',
    homeDescription: 'Efficient legal assistance for workplace rights & compliance matters',
    image: '/img/services/employment-law-card.webp',
    areaImage: '/img/services/employment-law-banner.webp',
    areaDescription:
      'We provide efficient Employment Law paralegal support for workplace rights and compliance matters. Our team assists attorneys with documentation, case management, and regulatory filings across employer and employee disputes. This ensures accuracy, timely responses, and streamlined case handling.',
    services: [
      { title: 'Workplace Dispute Documentation', description: 'Thorough documentation for employment disputes and grievances.' },
      { title: 'Regulatory Filing Support', description: 'Assisting with EEOC, DOL, and state agency filings.' },
      { title: 'Policy Review & Compliance', description: 'Reviewing employment policies for legal compliance.' },
      { title: 'Investigation Support', description: 'Organizing evidence and documentation for workplace investigations.' },
      { title: 'Litigation Preparation', description: 'Preparing case files for employment litigation matters.' },
      { title: 'Contract Review', description: 'Reviewing employment agreements, NDAs, and non-competes.' },
    ],
  },
  {
    slug: 'bankruptcy-paralegal-services',
    title: 'Bankruptcy Paralegal Services',
    shortTitle: 'Bankruptcy',
    tagline: 'Accurate, Compliant, and Timely Bankruptcy Case Preparation',
    description:
      'Our Bankruptcy Paralegal Services support attorneys with precise case preparation and statutory compliance. We assist in drafting petitions, reviewing financial data, and managing court deadlines. Our team ensures filings are accurate, organized, and court-ready. Designed to reduce errors, delays, and administrative burden throughout the bankruptcy process.',
    homeDescription: 'Accurate, Compliant, and Timely Bankruptcy Case Preparation',
    image: '/img/services/bankruptcy-card.webp',
    areaImage: '/img/services/bankruptcy-banner.webp',
    areaDescription:
      'Our Bankruptcy Paralegal Services support attorneys with precise case preparation and statutory compliance. We assist in drafting petitions, reviewing financial data, and managing court deadlines. Our team ensures filings are accurate, organized, and court-ready. Designed to reduce errors, delays, and administrative burden throughout the bankruptcy process.',
    services: [
      { title: 'Petition Drafting', description: 'Preparing Chapter 7, 11, and 13 bankruptcy petitions.' },
      { title: 'Financial Data Review', description: 'Analyzing financial documents and schedules for accuracy.' },
      { title: 'Court Deadline Management', description: 'Tracking and managing all court filing deadlines.' },
      { title: 'Creditor Communication', description: 'Managing correspondence with creditors and trustees.' },
      { title: 'Means Testing', description: 'Conducting means tests and eligibility assessments.' },
      { title: 'Document Organization', description: 'Organizing case files for court-ready presentation.' },
    ],
  },
  {
    slug: 'family-law-paralegal-support-services',
    title: 'Family Law Paralegal Support Services',
    shortTitle: 'Family Law',
    tagline: 'Compassionate assistance for sensitive legal matters',
    description:
      'We provide compassionate and precise Family Law paralegal support for sensitive legal matters. Our team assists attorneys with case documentation, court filings, and case management across divorce, custody, and domestic matters. We help ensure accuracy, confidentiality, and timely filings—allowing attorneys to focus on client advocacy.',
    homeDescription: 'Compassionate assistance for sensitive legal matters',
    image: '/img/services/family-law-card.webp',
    areaImage: '/img/services/family-law-banner.webp',
    areaDescription:
      'We provide compassionate and precise Family Law paralegal support for sensitive legal matters. Our team assists attorneys with case documentation, court filings, and case management across divorce, custody, and domestic matters. We help ensure accuracy, confidentiality, and timely filings—allowing attorneys to focus on client advocacy.',
    services: [
      { title: 'Divorce Documentation', description: 'Preparing petitions, financial disclosures, and settlement agreements.' },
      { title: 'Custody Case Support', description: 'Organizing evidence and documentation for custody proceedings.' },
      { title: 'Court Filing Management', description: 'Ensuring timely and accurate court filings.' },
      { title: 'Asset Documentation', description: 'Cataloging and organizing marital asset information.' },
      { title: 'Mediation Support', description: 'Preparing documents and summaries for mediation sessions.' },
      { title: 'Protective Order Assistance', description: 'Drafting and filing protective order petitions.' },
    ],
  },
  {
    slug: 'personal-immigration-paralegal-services',
    title: 'Personal Immigration Paralegal Services',
    shortTitle: 'Immigration',
    tagline: 'Reliable support for visas, citizenship & status documentation',
    description:
      'Our Immigration Paralegal Services support attorneys and individuals with meticulous, deadline-driven documentation. We assist with petitions, affidavits, and supporting records under strict regulatory standards. Designed to minimize errors, rejections, and processing delays.',
    homeDescription: 'Reliable support for visas, citizenship & status documentation',
    image: '/img/services/personal-immigration-card.webp',
    areaImage: '/img/services/personal-immigration-banner.webp',
    areaDescription:
      'Our Immigration Paralegal Services support attorneys and individuals with meticulous, deadline-driven documentation. We assist with petitions, affidavits, and supporting records under strict regulatory standards. Designed to minimize errors, rejections, and processing delays.',
    services: [
      { title: 'Visa Application Support', description: 'Preparing and organizing visa applications and supporting documents.' },
      { title: 'Petition Preparation', description: 'Drafting immigration petitions with required evidence.' },
      { title: 'Status Adjustment Filing', description: 'Managing adjustment of status applications and renewals.' },
      { title: 'Document Translation Coordination', description: 'Coordinating certified translations of foreign documents.' },
      { title: 'Compliance Monitoring', description: 'Tracking deadlines and ensuring regulatory compliance.' },
      { title: 'Case Status Tracking', description: 'Monitoring case progress and providing regular updates.' },
    ],
  },
];

export const paralegalServices = [
  {
    slug: 'document-review-paralegal-services',
    title: 'Document Review Paralegal Services',
    shortTitle: 'Document Review',
    tagline: 'Thorough and accurate legal document review services',
    description:
      'Detailed review of legal documents for relevance, accuracy, and compliance. Identification of key facts, evidence, privilege, and confidentiality issues. Supports efficient discovery, litigation strategy, and case preparation.',
    image: '/img/services/document-review-card.webp',
    services: [
      { title: 'Relevance Review', description: 'Identifying relevant documents for discovery and litigation.' },
      { title: 'Privilege Review', description: 'Flagging attorney-client privilege and work product materials.' },
      { title: 'Quality Control', description: 'Multi-tier review process ensuring accuracy and consistency.' },
      { title: 'Technology-Assisted Review', description: 'Leveraging advanced tools for efficient large-scale review.' },
      { title: 'Compliance Review', description: 'Ensuring documents meet regulatory and legal standards.' },
      { title: 'Redaction Services', description: 'Professional redaction of sensitive and privileged information.' },
    ],
  },
  {
    slug: 'contract-management-paralegal-services',
    title: 'Contract Management Paralegal Services',
    shortTitle: 'Contract Management',
    tagline: 'Comprehensive contract lifecycle management',
    description:
      'Drafting, reviewing, and organizing commercial and legal contracts. Identification of risks, inconsistencies, and compliance gaps. Ensures smooth contract administration and reduced legal exposure.',
    image: '/img/services/contract-management-card.webp',
    services: [
      { title: 'Contract Drafting', description: 'Preparing initial drafts of commercial and legal agreements.' },
      { title: 'Risk Identification', description: 'Analyzing contracts for potential risks and inconsistencies.' },
      { title: 'Compliance Gap Analysis', description: 'Reviewing contracts for regulatory compliance issues.' },
      { title: 'Contract Organization', description: 'Systematic organization and tracking of contract portfolios.' },
      { title: 'Renewal Management', description: 'Tracking deadlines and managing contract renewals.' },
      { title: 'Amendment Processing', description: 'Drafting and processing contract amendments and addenda.' },
    ],
  },
  {
    slug: 'legal-research-writing-paralegal-services',
    title: 'Legal Research & Writing Paralegal Services',
    shortTitle: 'Legal Research & Writing',
    tagline: 'In-depth research and professional legal writing',
    description:
      'Comprehensive legal research using reliable legal databases. Clear, concise legal writing for motions, pleadings, and case analysis. Supports informed decision-making and strong legal arguments.',
    image: '/img/services/legal-research-card.webp',
    services: [
      { title: 'Case Law Research', description: 'Thorough research of relevant case law and precedents.' },
      { title: 'Motion Drafting', description: 'Preparing motions, briefs, and pleadings.' },
      { title: 'Legal Memoranda', description: 'Writing comprehensive legal memoranda and analysis.' },
      { title: 'Statutory Research', description: 'Researching applicable statutes and regulations.' },
      { title: 'Citation Verification', description: 'Verifying and formatting legal citations.' },
      { title: 'Case Summaries', description: 'Preparing concise summaries of relevant cases.' },
    ],
  },
];

export const navLinks = [
  { label: 'Home', path: '/' },
  {
    label: 'Paralegal Services',
    path: '/paralegal-services',
    children: paralegalServices.map((s) => ({
      label: s.title,
      path: `/${s.slug}`,
    })),
  },
  {
    label: 'Area Of Practice',
    path: '/area-practice',
    children: practiceAreas.map((a) => ({
      label: a.title,
      path: `/${a.slug}`,
    })),
  },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact Us', path: '/contact-us' },
];
